import React from 'react';
import { motion, useTransform, MotionValue } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface BackgroundIcon {
  Icon: LucideIcon;
  label: string;
  top: string;
  left: string;
  size: number;
  speed: number;
  delay: number;
}

interface ParallaxBackgroundProps {
  icons: BackgroundIcon[];
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  scrollY: MotionValue<number>;
}

const ParallaxIcon: React.FC<{ 
  item: BackgroundIcon; 
  index: number;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  scrollY: MotionValue<number>;
}> = ({ item, mouseX, mouseY, scrollY }) => {
  const depth = (item.size / 40);
  
  const scrollYPos = useTransform(scrollY, [0, 800], [0, item.speed * 200 * depth]);
  const mouseXPos = useTransform(mouseX, [-0.5, 0.5], [depth * 40, depth * -40]);
  const mouseYPos = useTransform(mouseY, [-0.5, 0.5], [depth * 40, depth * -40]);
  const opacity = useTransform(scrollY, [0, 600], [0.12, 0]);

  return (
    <motion.div
      style={{ 
        top: item.top, 
        left: item.left,
        y: scrollYPos,
        x: mouseXPos,
        opacity: opacity,
        position: 'absolute',
        zIndex: 0,
        willChange: 'transform'
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.12 }}
      transition={{ duration: 0.8, delay: Math.min(item.delay, 0.5) }}
      className="group pointer-events-none select-none"
    >
      <div className="relative flex items-center justify-center">
        <item.Icon 
          size={item.size} 
          className="text-white/20 transition-colors duration-300" 
          strokeWidth={1}
        />
      </div>
    </motion.div>
  );
};

export const ParallaxBackground: React.FC<ParallaxBackgroundProps> = ({ icons, mouseX, mouseY, scrollY }) => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {icons.map((item, index) => (
        <ParallaxIcon 
          key={index} 
          item={item} 
          index={index} 
          mouseX={mouseX} 
          mouseY={mouseY} 
          scrollY={scrollY} 
        />
      ))}
    </div>
  );
};
